import React, { useMemo } from 'react';
import { InternalApiCustomListSchemaField, InternalApiCustomListSchemaFieldType } from '../../../api-internal';
import { ApiSanctionEntityType } from '../../../api';
import { ErrorBoundary } from 'react-error-boundary';
import { SalvBanner, SalvBannerType } from '@salvapp/ui-components';
import { Attribute, ObjectField } from '../../../common/attributes/types';
import { ContentLinkify } from '../../../common/ContentLinkify';
import { getHighlightedValue, getHighlightedText } from '../../../aml/sections/attributes/highlighters';

interface Props {
  entityType?: ApiSanctionEntityType;
  properties: {
    attributes: {
      [key: string]: Attribute;
    };
  };
  schemaFields?: InternalApiCustomListSchemaField[];
  highlight?: string;
  highlightColor: string;
  screenablePath?: string;
  fieldNames?: string[];
  showUnMappedProperties?: boolean;
}

const OBJECT = InternalApiCustomListSchemaFieldType.OBJECT;

const ScreeningListMatch = (props: Props) => {
  const {
    entityType,
    properties,
    schemaFields,
    highlight,
    highlightColor,
    screenablePath,
    fieldNames,
    showUnMappedProperties,
  } = props;

  const parentFields = schemaFields?.filter((field) => !field.parentId);
  const screenablePathParts = useMemo(() => {
    return [...new Set(screenablePath?.split(' '))];
  }, [screenablePath]);

  const displayMatch = (fieldName: string, text?: string) => {
    if (screenablePathParts.includes(fieldName)) {
      return getHighlightedText({
        text: text || '',
        highlight,
        color: highlightColor,
      });
    } else {
      return text || '';
    }
  };

  const renderMultiValue = (field: InternalApiCustomListSchemaField) => {
    return (
      <p key={field.id}>
        <span className='property'>{field.displayName}: </span>
        {getHighlightedValue({
          text: getFieldValue<string[]>(field)?.join(', ') || '',
          highlight,
          color: highlightColor,
        })}
      </p>
    );
  };

  const renderChildrenValues = (
    field: InternalApiCustomListSchemaField,
    attributeList?: ObjectField[],
    schemaFields?: InternalApiCustomListSchemaField[]
  ) => {
    const parentScreenablePath = field.isMulti ? field.fieldName + '[].' : field.fieldName + '.';
    return (
      <p key={field.id}>
        <span className='property'>{field.displayName}: </span>
        {attributeList
          ?.filter((attribute) => !!attribute)
          ?.map((attribute, index: number) => {
            return (
              <span key={`${field.id}-${index}`} className='ms-2 mb-3 d-block'>
                {schemaFields
                  ?.filter((schema) => !!schema)
                  ?.filter((schema) => attribute[schema.fieldName])
                  ?.map((schema) => {
                    return (
                      <span key={schema.id} className='mb-0 d-block'>
                        <span className='property'>{schema.displayName}: </span>
                        {displayMatch(parentScreenablePath + schema.fieldName, attribute[schema.fieldName])}
                      </span>
                    );
                  })}
              </span>
            );
          })}
      </p>
    );
  };

  const getFieldValue = <T extends Attribute>(field: InternalApiCustomListSchemaField) => {
    if (!properties.attributes) {
      return undefined;
    }
    return properties.attributes[field.fieldName] as T;
  };

  const Fallback = () => (
    <>
      <SalvBanner
        type={SalvBannerType.DANGER}
        message={"This Record does not adhere to its Custom list's Schema and failed to be properly formatted"}
      />
      {JSON.stringify(properties.attributes)}
    </>
  );

  const RenderRows = () => {
    return (
      <ContentLinkify>
        {parentFields
          ?.filter((field) => !!getFieldValue(field))
          ?.map((field) => {
            if (field.isMulti && field.type !== OBJECT) {
              return renderMultiValue(field);
            } else if (field.isMulti && field.type === OBJECT) {
              const childSchemaFields = schemaFields?.filter(
                (f: InternalApiCustomListSchemaField) => f.parentId == field.id
              );
              return renderChildrenValues(field, getFieldValue<ObjectField[]>(field), childSchemaFields);
            } else if (field.type === OBJECT) {
              const children = schemaFields?.filter((f) => f.parentId == field.id);
              const fieldValue = getFieldValue<ObjectField>(field);
              return renderChildrenValues(field, fieldValue ? [fieldValue] : undefined, children);
            }

            return (
              <p key={field.id}>
                <span className='property'>{field.displayName}: </span>
                {displayMatch(field.fieldName, getFieldValue(field))}
              </p>
            );
          })}
      </ContentLinkify>
    );
  };

  const renderUnMappedProperties = () => {
    if (!properties.attributes) {
      return null;
    }
    return Object.keys(properties.attributes)
      ?.filter((key) => !fieldNames?.includes(key))
      .map((key) => {
        return (
          <p key={key}>
            <span className='property'>{key}: </span>
            {getHighlightedValue({
              text: JSON.stringify(properties.attributes[key]),
              highlight,
              color: highlightColor,
            })}
          </p>
        );
      });
  };

  return (
    <>
      {entityType && (
        <p>
          <b className='match-type'>{entityType.toLowerCase()}:</b>
        </p>
      )}
      <ErrorBoundary fallbackRender={Fallback}>
        <RenderRows />
      </ErrorBoundary>
      {showUnMappedProperties && renderUnMappedProperties()}
    </>
  );
};
export default ScreeningListMatch;
